import { FC, Fragment, useMemo } from 'react'
import gql from 'graphql-tag'
import pick from 'lodash/pick'
import Image from 'next/image'
import { useRouter } from 'next/router'
import styled, { css } from 'styled-components'
import {
  ActivityListCardItem_ActivityFragment,
  ActivityListCardItem_CurrentUserFragment,
  ActivityListCardItem_SecondInfoText_ActivityFragment,
} from 'generated/graphql'
import Skeleton from '@app.components/Skeleton'
import { TEAM_RECRUIT_VISIBLE_ACTIVITY_TYPE } from '@app.feature/activityDetail/constant/activity.constant'
import useIsMobile from '@app.modules/hooks/useIsMobile'
import useIsWebview from '@app.modules/hooks/useIsWebview'
import { getWebviewHref } from '@app.modules/util'
import RecommendedTag from 'app.components/tag/RecommendedTag'
import { mobileBreakPoint } from 'app.styled/media'
import ManagerReplyBadge from 'components/ActivityListCardItem/components/ManagerReplyBadge'
import Link, { LinkProps } from 'components/Link'
import { ActivityTypeID } from 'constants/enums'
import { imageLoader, optimizeImageUrl } from 'utils/optimizeImageUrl'
import ScrapBoxButton from './ScrapBoxButton'
import SecondInfoText, { SecondInfoTextProps } from './SecondInfoText'
import TeamRecruitBadge from './components/TeamRecruitBadge'

gql`
  fragment ActivityListCardItem_currentUser on User {
    ...ActivityScrap_currentUser
  }
  fragment ActivityListCardItem_activity on Activity {
    id
    title
    type
    thumbnailImage {
      id
      url
    }
    logoImage {
      id
      url
    }
    manager {
      id
      isStaff
      channel {
        id
        url
      }
    }
    chatRoom {
      id
    }
    managerReplies {
      id
    }
    organizationName
    scrapCount
    isScrapped
    teamRecruitPostCount
    ...ActivityListCardItem_SecondInfoText_activity
    ...ActivityScrap_activity
  }
`

interface IFCardImgProps {
  width?: number | string
  src: string
  alt: string
}

interface IFActivityListCardItem {
  activity?: ActivityListCardItem_ActivityFragment
  currentUser?: ActivityListCardItem_CurrentUserFragment
  isLoading?: boolean
  className?: string
  cardTitle?: string | null
  cardImageProps?: IFCardImgProps
  linkProps?: Partial<LinkProps>
  managerBadgeOn?: boolean
  teamRecruitBadgeOn?: boolean
  secondinfoTextProps?: Partial<SecondInfoTextProps>
  isRecommend?: boolean
  onClick?: () => void
}

const ActivityListCardItem: FC<IFActivityListCardItem> = ({
  activity,
  currentUser,
  isLoading,
  className,
  cardImageProps,
  linkProps,
  managerBadgeOn = true,
  teamRecruitBadgeOn = false,
  secondinfoTextProps,
  cardTitle,
  isRecommend,
  onClick,
}) => {
  const router = useRouter()
  const isWebview = useIsWebview()
  const isMobile = useIsMobile()
  const isRecruit = activity?.type === ActivityTypeID.RECRUIT

  const isTeamRecruitAvailable =
    activity?.type &&
    TEAM_RECRUIT_VISIBLE_ACTIVITY_TYPE.includes(activity.type) &&
    activity?.teamRecruitPostCount > 0

  const ActivityItemLinkProps = {
    href: '/activity/[id]',
    as: `/activity/${activity?.id}`,
    naked: true,
    onClick: () => onClick && onClick(),
  }

  const secondInfos = pick(activity, [
    'recruitCloseAt',
    'recruitType',
    'replyCount',
    'viewCount',
  ]) as ActivityListCardItem_SecondInfoText_ActivityFragment

  const imageUrl =
    cardImageProps?.src ||
    (isRecruit ? activity?.logoImage?.url : activity?.thumbnailImage?.url) ||
    ''

  const imageWidth = useMemo(() => {
    if (cardImageProps?.width) {
      return cardImageProps?.width as number
    } else {
      return isMobile ? 300 : 250
    }
  }, [cardImageProps?.width, isMobile])

  const handleClickOrganization = () => {
    if (activity?.manager?.channel) {
      router.push(
        getWebviewHref(`/channel/${activity.manager.channel.url}`, isWebview),
      )
    }
  }

  return (
    <StyledWrapper className={className}>
      <div className="image-wrapper">
        {isLoading && <Skeleton className="skeleton-rect" />}
        {!isLoading && (
          <Fragment>
            <Link
              className="image-link"
              {...ActivityItemLinkProps}
              {...linkProps}
            >
              <Image
                loader={imageLoader}
                key={`activity-image-${activity?.id}`}
                alt={cardImageProps?.alt || activity?.title || ''}
                className="activity-image"
                width={imageWidth}
                height={imageWidth}
                src={optimizeImageUrl({
                  url: imageUrl,
                  width: imageWidth,
                  height: 'auto',
                })}
                objectFit="cover"
              />
            </Link>
            <ScrapBoxButton activity={activity} currentUser={currentUser} />
            <div className="badge-wrapper">
              {managerBadgeOn && !activity?.manager?.isStaff && (
                <ManagerReplyBadge
                  managerRepliesCount={activity?.managerReplies?.length || 0}
                />
              )}
              {teamRecruitBadgeOn && isTeamRecruitAvailable && (
                <TeamRecruitBadge
                  teamRecruitPostCount={activity.teamRecruitPostCount}
                />
              )}
            </div>
          </Fragment>
        )}
      </div>
      <div className="card-content">
        {isLoading && (
          <Fragment>
            <Skeleton height={36} width="100%" className="title-skeleton" />
            <Skeleton height={12} width="100%" />
            <div className="skeleton-wrapper">
              <Skeleton height={12} width="30%" />
              <Skeleton height={12} width="30%" />
              <Skeleton height={12} width="30%" />
            </div>
          </Fragment>
        )}
        {!isLoading && (
          <Fragment>
            <Link {...ActivityItemLinkProps}>
              <h5 className="activity-title">
                {isRecommend && <RecommendedTag />}
                {cardTitle || activity?.title || '-'}
              </h5>
            </Link>
            <div>
              <p
                className="organization-name"
                style={{
                  cursor: activity?.manager?.channel ? 'pointer' : 'default',
                }}
                onClick={handleClickOrganization}
              >
                {activity?.organizationName || '-'}
              </p>
              <SecondInfoText
                activityInfos={secondInfos}
                {...secondinfoTextProps}
              />
            </div>
          </Fragment>
        )}
      </div>
    </StyledWrapper>
  )
}

export default ActivityListCardItem

const StyledWrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    .image-wrapper {
      position: relative;
      height: 0;
      overflow: hidden;
      padding-top: 100%;
      border: 1px solid #e8e8e8;
      border-radius: 5px;

      .skeleton-rect {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-radius: 5px;
      }
      .image-link {
        position: absolute;
        top: 0;
        left: 0;
        display: grid;
        width: 100%;
        height: 100%;

        overflow: hidden;

        .activity-image {
          width: 100% !important;
          height: auto !important;
          min-height: auto !important;
        }
      }

      .badge-wrapper {
        display: flex;
        gap: 4px;
        position: absolute;
        left: ${theme.isMobile ? '5px' : '8px'};
        bottom: ${theme.isMobile ? '5px' : '8px'};
      }
    }
    .card-content {
      display: grid;
      gap: 5px;
      padding: 10px 0 0 0;

      .title-skeleton {
        margin: 10px 0px;
      }
      .skeleton-wrapper {
        display: flex;
        gap: 10px;
      }
      .activity-title {
        display: -webkit-box;
        height: 45px;
        overflow: hidden;
        text-overflow: ellipsis;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        word-break: break-all;
        font-size: 16px;
        font-weight: bold;
        margin-bottom: 5px;
        ${mobileBreakPoint} {
          height: 41px;
          font-size: 14px;
        }
      }
      .organization-name {
        font-size: 12px;
        font-weight: 500;
        margin-bottom: 5px;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 1;
      }
    }
  `}
`
